"use client";
import { useCallback, useEffect, useState } from "react"
import { useHotkeys } from "react-hotkeys-hook"
import { TextSelection } from "@tiptap/pm/state"

// --- Hooks ---
import { useTiptapEditor } from "@/tiptap/hooks/use-tiptap-editor"
import { useIsBreakpoint } from "@/tiptap/hooks/use-is-breakpoint"

// --- Lib ---
import {
  isMarkInSchema,
  isNodeTypeSelected,
} from "@/tiptap/lib/tiptap-utils"

// --- Icons ---
import { TextColorSmallIcon } from "@/tiptap/icons/text-color-small-icon"

export const COLOR_TEXT_SHORTCUT_KEY = "mod+shift+t"
export const TEXT_COLORS = [
  {
    label: "Default text",
    value: "var(--tt-color-text)",
    border: "var(--tt-color-text-contrast)",
  },
  {
    label: "Gray text",
    value: "var(--tt-color-text-gray)",
    border: "var(--tt-color-text-gray-contrast)",
  },
  {
    label: "Brown text",
    value: "var(--tt-color-text-brown)",
    border: "var(--tt-color-text-brown-contrast)",
  },
  {
    label: "Orange text",
    value: "var(--tt-color-text-orange)",
    border: "var(--tt-color-text-orange-contrast)",
  },
  {
    label: "Yellow text",
    value: "var(--tt-color-text-yellow)",
    border: "var(--tt-color-text-yellow-contrast)",
  },
  {
    label: "Green text",
    value: "var(--tt-color-text-green)",
    border: "var(--tt-color-text-green-contrast)",
  },
  {
    label: "Blue text",
    value: "var(--tt-color-text-blue)",
    border: "var(--tt-color-text-blue-contrast)",
  },
  {
    label: "Purple text",
    value: "var(--tt-color-text-purple)",
    border: "var(--tt-color-text-purple-contrast)",
  },
  {
    label: "Pink text",
    value: "var(--tt-color-text-pink)",
    border: "var(--tt-color-text-pink-contrast)",
  },
  {
    label: "Red text",
    value: "var(--tt-color-text-red)",
    border: "var(--tt-color-text-red-contrast)",
  },
]

const NESTED_LIST_TYPES = new Set(["bulletList", "orderedList", "taskList"])

function textStyleColor(node) {
  const mark = node.marks.find((item) => item.type.name === "textStyle")
  return mark?.attrs?.color ?? null
}

function mergeStyleColor(style, color) {
  const declarations = String(style || "")
    .split(";")
    .map((part) => part.trim())
    .filter((part) => part.length > 0 && !/^color\s*:/i.test(part))

  if (color) {
    declarations.push(`color: ${color}`)
  }

  return declarations.length > 0 ? `${declarations.join("; ")};` : null
}

/**
 * Shared textStyle color of a list item's own text (nested lists skipped).
 * Returns null when the item has no text or the colors are mixed.
 */
export function uniformOwnTextColor(listItem) {
  let sharedColor
  let hasText = false

  listItem.descendants((node) => {
    if (NESTED_LIST_TYPES.has(node.type.name)) return false
    if (!node.isText || !node.text) return true

    hasText = true
    const color = textStyleColor(node)
    if (sharedColor === undefined) {
      sharedColor = color
      return true
    }

    if (sharedColor !== color) {
      sharedColor = null
    }

    return true
  })

  return hasText ? sharedColor : null
}

/**
 * After a color mark toggle, set or clear `color` on selected list items so
 * bullets/numbers match only when every own-text node shares one color.
 */
export function syncListItemMarkerColors(tr) {
  const { from, to } = tr.selection
  const updates = []

  tr.doc.nodesBetween(from, to, (node, pos) => {
    if (node.type.name !== "listItem") return true

    const nextStyle = mergeStyleColor(node.attrs.style, uniformOwnTextColor(node))
    if (nextStyle !== (node.attrs.style || null)) {
      updates.push({ pos, attrs: { ...node.attrs, style: nextStyle } })
    }

    return true
  })

  updates.forEach(({ pos, attrs }) => {
    tr.setNodeMarkup(pos, undefined, attrs)
  })

  return tr
}

export function applyTextColor(editor, textColor) {
  return editor
    .chain()
    .focus()
    .toggleMark("textStyle", { color: textColor })
    .command(({ tr }) => {
      syncListItemMarkerColors(tr)
      return true
    })
    .run()
}

export function selectCurrentTextBlockContent(editor) {
  const { selection, doc } = editor.state
  if (!selection.empty) return false

  const $pos = selection.$from

  for (let depth = $pos.depth; depth >= 0; depth--) {
    const node = $pos.node(depth)
    if (!node.isTextblock) continue
    if (!node.textContent.trim()) return false

    const from = $pos.start(depth)
    const to = from + node.nodeSize - 2
    if (from >= to) return false

    const nextSelection = TextSelection.between(doc.resolve(from), doc.resolve(to), 1)
    if (!selection.eq(nextSelection)) {
      editor.view.dispatch(editor.state.tr.setSelection(nextSelection))
    }

    return true
  }

  return false
}

/**
 * Checks if text color can be toggled in the current editor state
 */
export function canColorText(editor) {
  if (!editor || !editor.isEditable) return false
  if (
    !isMarkInSchema("textStyle", editor) ||
    isNodeTypeSelected(editor, ["image"])
  )
    return false

  try {
    return editor.can().setMark("textStyle", { color: "currentColor" });
  } catch {
    return false
  }
}

/**
 * Checks if text color is active in the current selection
 */
export function isColorTextActive(editor, textColor) {
  if (!editor || !editor.isEditable) return false
  return editor.isActive("textStyle", { color: textColor });
}

/**
 * Determines if the color text button should be shown
 */
export function shouldShowButton(props) {
  const { editor, hideWhenUnavailable } = props

  if (!editor) return false

  if (!hideWhenUnavailable) {
    return true
  }

  if (!editor.isEditable) return false

  if (!isMarkInSchema("textStyle", editor)) return false

  if (!editor.isActive("code")) {
    return canColorText(editor);
  }

  return true
}

/**
 * Custom hook that provides color text functionality for Tiptap editor
 *
 * @example
 * ```tsx
 * // Simple usage with required textColor
 * function MySimpleTextColorButton() {
 *   const { isVisible, handleColorText, isActive } = useColorText({
 *     textColor: "red",
 *     label: "Red Text",
 *   })
 *
 *   if (!isVisible) return null
 *
 *   return (
 *     <button
 *       onClick={handleColorText}
 *       style={{ color: isActive ? "red" : "inherit" }}
 *     >
 *       Red Text
 *     </button>
 *   )
 * }
 *
 * // Advanced usage
 * function MyAdvancedTextColorButton() {
 *   const { isVisible, handleColorText, label, isActive } = useColorText({
 *     editor: myEditor,
 *     textColor: "#ff0000",
 *     label: "Apply Red",
 *     hideWhenUnavailable: true,
 *     onApplied: ({ color }) => console.log("Applied:", color),
 *   })
 *
 *   if (!isVisible) return null
 *
 *   return (
 *     <MyButton
 *       onClick={handleColorText}
 *       aria-label={label}
 *       data-active={isActive}
 *     >
 *       Apply Text Color
 *     </MyButton>
 *   )
 * }
 * ```
 */
export function useColorText(config) {
  const {
    editor: providedEditor,
    label,
    textColor,
    hideWhenUnavailable = false,
    onApplied,
  } = config

  const { editor } = useTiptapEditor(providedEditor)
  const isMobile = useIsBreakpoint()
  const [isVisible, setIsVisible] = useState(true)
  const canColorTextState = canColorText(editor)
  const isActive = isColorTextActive(editor, textColor)

  useEffect(() => {
    if (!editor) return

    const handleSelectionUpdate = () => {
      setIsVisible(shouldShowButton({ editor, hideWhenUnavailable }))
    }

    handleSelectionUpdate()

    editor.on("selectionUpdate", handleSelectionUpdate)

    return () => {
      editor.off("selectionUpdate", handleSelectionUpdate)
    };
  }, [editor, hideWhenUnavailable])

  const handleColorText = useCallback(() => {
    if (!editor || !canColorTextState) return false

    if (editor.state.storedMarks) {
      const textStyleMarkType = editor.schema.marks.textStyle
      if (textStyleMarkType) {
        editor.view.dispatch(editor.state.tr.removeStoredMark(textStyleMarkType))
      }
    }

    setTimeout(() => {
      selectCurrentTextBlockContent(editor)

      const success = applyTextColor(editor, textColor)
      if (success) {
        onApplied?.({ color: textColor, label })
      }
      return success
    }, 0)
  }, [editor, canColorTextState, textColor, onApplied, label])

  useHotkeys(COLOR_TEXT_SHORTCUT_KEY, (event) => {
    event.preventDefault()
    handleColorText()
  }, {
    enabled: isVisible && canColorTextState,
    enableOnContentEditable: !isMobile,
    enableOnFormTags: true,
  })

  return {
    isVisible,
    isActive,
    handleColorText,
    canColorText: canColorTextState,
    label: label || `Color text to ${textColor}`,
    shortcutKeys: COLOR_TEXT_SHORTCUT_KEY,
    Icon: TextColorSmallIcon,
  }
}
